<template>
    <grid
        ref="grid"
        :columnDefs="columnDefs"
        :fetchCallback="fetchCallback"
        :maxBlocksInCache="null"
    >
        <date-range-picker
            :range-options="predefinedRanges"
            :value="dateRange"
            :date-format="userUnicodeDateFormat"
            @input="handleDaterangeChange"
        ></date-range-picker>

        <b-button-group>
            <search-multiselect
                v-model="packagesSelected"
                variant="primary"
                :options="packageOptions"
                placeholder="Packages"
                class="mr-1"
                :icon="['fas', 'cube']"
                :responsiveness="true"
            ></search-multiselect>
        </b-button-group>
        <b-button-group aria-label="Export table data">
            <responsive-button
                :icon="['fas', 'file-excel']"
                :href="exportExcelHref"
                @click="generatingExcelReportSuccessToast"
                variant="primary"
            >
            </responsive-button>
        </b-button-group>
        <b-button-group>
            <more-filters-dropdown :icon="['fas', 'circle-info']">
                <responsive-button
                    :clickable="false"
                    :responsiveness="false"
                    variant="secondary"
                >
                    <div
                        class="
                            d-flex
                            justify-content-between
                            align-items-center
                        "
                    >
                        <div class="text-left">Total Subscriptions</div>
                        <div>
                            <b-badge variant="light">{{
                                totalSubscriptions
                            }}</b-badge>
                        </div>
                    </div>
                </responsive-button>
                <responsive-button
                    :clickable="false"
                    :responsiveness="false"
                    variant="secondary"
                >
                    <div
                        class="
                            d-flex
                            justify-content-between
                            align-items-center
                        "
                    >
                        <div class="text-left">Active Subscriptions</div>
                        <div>
                            <b-badge variant="light">{{
                                activeSubscriptions
                            }}</b-badge>
                        </div>
                    </div>
                </responsive-button>
                <responsive-button
                    :clickable="false"
                    :responsiveness="false"
                    variant="secondary"
                >
                    <div
                        class="
                            d-flex
                            justify-content-between
                            align-items-center
                        "
                    >
                        <div class="text-left">Trial Subscriptions</div>
                        <div>
                            <b-badge variant="light">{{
                                trialSubscriptions
                            }}</b-badge>
                        </div>
                    </div>
                </responsive-button>
                <responsive-button
                    :clickable="false"
                    :responsiveness="false"
                    variant="secondary"
                >
                    <div
                        class="
                            d-flex
                            justify-content-between
                            align-items-center
                        "
                    >
                        <div class="text-left">Cancelled Subscriptions</div>
                        <div>
                            <b-badge variant="light">{{
                                cancelledSubscriptions
                            }}</b-badge>
                        </div>
                    </div>
                </responsive-button>
                <responsive-button
                    :clickable="false"
                    :responsiveness="false"
                    variant="secondary"
                >
                    <div
                        class="
                            d-flex
                            justify-content-between
                            align-items-center
                        "
                    >
                        <div class="text-left">Suspended Subscriptions</div>
                        <div>
                            <b-badge variant="light">{{
                                suspendedSubscriptions
                            }}</b-badge>
                        </div>
                    </div>
                </responsive-button>
            </more-filters-dropdown>
        </b-button-group>
        <responsive-button
            :icon="['fas', 'arrows-rotate']"
            variant="primary"
            @click="refresh"
            class="ml-1"
        >
            Refresh
        </responsive-button>
    </grid>
</template>

<script>
import { mapGetters } from 'vuex';
import format from 'date-fns/format';
import API from '../../../../api';
import { escapeHTML } from '../../../../utils/strings';
import DateRangePicker, {
    ALL_DATES,
    LAST_MONTH,
    LAST_YEAR,
    nameToRange,
    THIS_MONTH,
    THIS_YEAR,
    TODAY,
    YESTERDAY,
} from '../../../generic/date-range-picker/date-range-picker.vue';
import Grid from '../../../generic/grid/grid.vue';
import ResponsiveButton from '../../../generic/responsive-button/responsive-button.vue';
import ActionsCellRenderer from './actions-cell-renderer.vue';
import SearchMultiselect from '../../../generic/search-multiselect/search-multiselect.vue';
import MoreFiltersDropdown from '../../../generic/more-filters-dropdown/more-filters-dropdown.vue';
import {
    SUBSCRIBER_SUBSCRIPTIONS_GRID_ENDPOINT,
    SUBSCRIBER_SUBSCRIPTIONS_EXCEL_ENDPOINT,
    SUBSCRIBER_SUBSCRIPTIONS_STATISTICS_ENDPOINT,
    SUBSCRIBER_SUBSCRIPTIONS_PACKAGES_ENDPOINT,
} from '../../../../endpoints';

export default {
    name: 'SubscriberSubscriptionList',
    components: {
        Grid,
        ResponsiveButton,
        DateRangePicker,
        MoreFiltersDropdown,
        SearchMultiselect,
        actionsCellRenderer: ActionsCellRenderer,
    },
    computed: {
        ...mapGetters('header', ['userUnicodeDateFormat']),
        fetchCallback() {
            return ({ offset, limit, sort, desc, search }) => {
                this.offset = offset;
                this.limit = limit;
                this.sort = sort;
                this.desc = desc;
                this.search = search;
                return API.get(SUBSCRIBER_SUBSCRIPTIONS_GRID_ENDPOINT(), {
                    params: {
                        offset,
                        limit,
                        sort,
                        desc,
                        search,
                        from_date: this.fromDateRequest,
                        to_date: this.toDateRequest,
                        packages: this.packagesRequest,
                        addon: this.addon,
                    },
                });
            };
        },
        packagesRequest() {
            const packageStatus = new Set();
            this.packagesSelected.forEach((pkg) => {
                packageStatus.add(pkg);
            });
            return [...packageStatus].join(',');
        },
        fromDateRequest() {
            return format(this.dateRange.from, this.dateRequestFormat);
        },
        toDateRequest() {
            return format(this.dateRange.to, this.dateRequestFormat);
        },
        exportExcelHref() {
            let excelUrl =
                `${SUBSCRIBER_SUBSCRIPTIONS_EXCEL_ENDPOINT()}` +
                `?offset=${this.offset}` +
                `&limit=${this.limit}` +
                `&sort=${this.sort}` +
                `&desc=${this.desc}` +
                `&search=${this.search}` +
                `&from_date=${this.fromDateRequest}` +
                `&to_date=${this.toDateRequest}`;

            if (this.packagesSelected) {
                excelUrl += `&packages=${this.packagesRequest}`;
            }
            if (this.addon) {
                excelUrl += `&addon=${this.addon}`;
            }

            return excelUrl;
        },
        packageOptions() {
            return this.packages.map((item) => ({
                text:
                    item.package_status === 'ACTIVE'
                        ? `${item.name}`
                        : `${item.name} (${item.package_status})`,
                value: item.id,
            }));
        },
        currentRoutePackage() {
            return parseInt(this.$route.query.package);
        },
        currentRouteAddon() {
            return parseInt(this.$route.query.addon);
        },
    },
    data() {
        return {
            offset: 0,
            limit: 20,
            sort: 'id',
            desc: false,
            search: '',
            totalSubscriptions: null,
            activeSubscriptions: null,
            trialSubscriptions: null,
            cancelledSubscriptions: null,
            suspendedSubscriptions: null,
            packages: [],
            packagesSelected: [],
            addon: null,
            columnDefs: [
                {
                    field: 'id',
                    headerName: 'ID',
                    minWidth: 100,
                    maxWidth: 100,
                    sort: 'desc',
                },
                {
                    field: 'user.id',
                    headerName: 'User ID',
                    minWidth: 120,
                    maxWidth: 120,
                    
                },                
                {
                    field: 'user.email',
                    headerName: '',
                    autoHeight: true,
                    minWidth: 300,
                    cellStyle: { whiteSpace: 'normal', wordBreak: 'break-all' },
                    cellRenderer: ({ data }) => {
                        return data.user.full_name
                            ? `${escapeHTML(data.user.email)}<br />${escapeHTML(
                                  data.user.full_name
                              )}`
                            : escapeHTML(data.user.email);
                    },
                },
                {
                    field: 'started',
                    headerName: 'Signup',
                    type: 'dateColumn',
                    minWidth: 120,
                    maxWidth: 120,
                },

                {
                    field: 'package.name',
                    headerName: 'Package',
                    minWidth: 200,
                    autoHeight: true,
                    cellStyle: { whiteSpace: 'normal', wordBreak: 'break-all' },
                },
                {
                    field: 'package.amount',
                    headerName: 'Price',
                },
                {
                    field: 'total_paid',
                    headerName: 'Total',
                    sortable: false,
                },
                {
                    field: 'trial_to',
                    headerName: 'Expires',
                    type: 'dateColumn',
                    minWidth: 120,
                    maxWidth: 120,
                    sortable: false,
                },
                {
                    field: 'next_billing_date',
                    headerName: 'Billing',
                    type: 'dateColumn',
                    sortable: false,
                    minWidth: 120,
                    maxWidth: 120,
                },
                {
                    field: 'status',
                    headerName: 'Status',
                    minWidth: 150,
                    cellStyle: { whiteSpace: 'normal', wordBreak: 'break-all' },
                },
                {
                    field: 'last_status_change',
                    headerName: 'Change',
                    type: 'dateColumn',
                    minWidth: 120,
                    maxWidth: 120,
                },
                {
                    field: 'user.last_login',
                    headerName: 'Last Login',
                    type: 'dateColumn',
                    minWidth: 120,
                    maxWidth: 120,
                },
                {
                    headerName: '',
                    maxWidth: 220,
                    minWidth: 220,
                    resizable: false,
                    sortable: false,
                    cellStyle: { textAlign: 'center' },
                    cellRenderer: 'actionsCellRenderer',
                    cellRendererParams: {
                        fetchStatistics: this.fetchStatistics,
                        refresh: this.refresh,
                    },
                },
            ],
            dateRange: nameToRange(ALL_DATES),
            dateRequestFormat: 'yyyy-MM-dd',
            predefinedRanges: [
                TODAY,
                YESTERDAY,
                THIS_MONTH,
                LAST_MONTH,
                THIS_YEAR,
                LAST_YEAR,
                ALL_DATES,
            ],
        };
    },
    mounted() {
        if (this.currentRoutePackage) {
            this.packagesSelected = [this.currentRoutePackage];
        }
        if (this.currentRouteAddon) {
            this.addon = this.currentRouteAddon;
        }
        (async () => {
            this.$parent.$emit('busy', true);
            try {
                await Promise.all([
                    this.fetchStatistics(),
                    this.fetchPackages(),
                ]);
            } finally {
                this.$parent.$emit('busy', false);
            }
        })();
    },
    methods: {
        handleDaterangeChange(value) {
            this.dateRange = { ...value };
            this.refresh();
        },
        refresh() {
            this.$refs.grid.refreshDataSource();
        },
        async fetchStatistics() {
            try {
                const response = await API.get(
                    SUBSCRIBER_SUBSCRIPTIONS_STATISTICS_ENDPOINT()
                );
                this.totalSubscriptions = response.data.total_subscriptions;
                this.activeSubscriptions =
                    response.data.total_paid_subscriptions;
                this.trialSubscriptions =
                    response.data.total_trial_subscriptions;
                this.cancelledSubscriptions =
                    response.data.total_cancelled_subscriptions;
                this.suspendedSubscriptions =
                    response.data.total_suspended_subscriptions;
            } catch {
                this.errorToast({ text: 'Could not fetch statistics.' });
            }
        },
        async fetchPackages() {
            try {
                const response = await API.get(
                    SUBSCRIBER_SUBSCRIPTIONS_PACKAGES_ENDPOINT()
                );
                this.packages = response.data;
            } catch {
                this.errorToast({ text: 'Could not fetch packages.' });
            }
        },
    },
    watch: {
        packagesSelected() {
            this.refresh();
        },
    },
};
</script>
